import type { TunaEditor } from './plugin-types.js'

/**
 * Browser-only applicator for an agent command.
 *
 * Given a live editor, a resolved Slate path (computed from a CommandAnchor),
 * and the command's args, performs the actual mutation. Lives separately
 * from `AgentCommandSpec` (which is shared with the Lambda) so applicators
 * are free to import Plate/Slate without polluting the backend bundle.
 *
 * `path` is the resolved Slate path — agent's `resolveAnchor` translates
 * the stable block-id-based CommandAnchor into a concrete numeric path
 * before invocation. The applicator does not see the anchor.
 */
export type AgentCommandApplyFn = (
  editor: TunaEditor,
  path: number[],
  args: Record<string, unknown>
) => void

const applicators = new Map<string, AgentCommandApplyFn>()

/**
 * Register a browser-side applicator for an agent command.
 *
 * Throws on duplicate registration to catch developer mistakes. Plugin
 * `index.tsx` files use a `getAgentCommandApplicator(id) ? skip : register`
 * guard so hot-reload and duplicate imports don't trip the check.
 */
export const registerAgentCommandApplicator = (commandId: string, fn: AgentCommandApplyFn): void => {
  if (applicators.has(commandId)) {
    throw new Error(`agent-command-applicator: '${commandId}' is already registered`)
  }
  applicators.set(commandId, fn)
}

export const getAgentCommandApplicator = (commandId: string): AgentCommandApplyFn | undefined =>
  applicators.get(commandId)

/**
 * Test helper. Exported from the barrel for cross-package tests.
 * @internal
 */
export const resetApplicatorRegistryForTests = (): void => {
  applicators.clear()
}
